import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useVideosWithEntities } from "../../hooks/useVideosWithEntities";
import { useAuth } from "../../utils/AuthContext";
import { VideoThumbnail } from "../../types/cms";
import VideoCard from "./VideoCard";
import VideoQuickView from "./VideoQuickView";

const PAGE_SIZE = 24;

const SkeletonVideoCard: React.FC = () => (
  <div className="flex flex-col gap-4 animate-pulse">
    <div className="bg-gray-300 rounded-md aspect-video w-full" />
    <div className="flex gap-4 items-start">
      <div className="bg-gray-300 rounded-full w-10 h-10" />
      <div className="flex flex-col flex-1 gap-2">
        <div className="bg-gray-300 rounded-md h-4 w-3/4" />
        <div className="bg-gray-300 rounded-md h-3 w-1/2" />
      </div>
    </div>
  </div>
);

interface VideosProps {
  selectedType: string;
  /** Industry filter, "All" for everything. */
  selectedCategory: string;
}

const Videos: React.FC<VideosProps> = ({ selectedType, selectedCategory }) => {
  const { isAdmin } = useAuth();
  const { videos, loading, error } = useVideosWithEntities("public");
  const location = useLocation();
  const searchQuery = new URLSearchParams(location.search).get("query")?.trim().toLowerCase() ?? "";

  const [openVideo, setOpenVideo] = useState<VideoThumbnail | null>(null);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const filteredVideos = useMemo(
    () =>
      videos.filter((video) => {
        if (selectedCategory !== "All" && !video.industries.includes(selectedCategory)) return false;
        if (selectedType === "Styleframes" && !video.styleframesURLs.length) return false;
        if (!searchQuery) return true;
        return [video.title, video.videoFor?.name, video.producedBy?.name, ...video.tags]
          .filter(Boolean)
          .some((field) => field!.toLowerCase().includes(searchQuery));
      }),
    [videos, selectedCategory, selectedType, searchQuery],
  );

  // Reset pagination when filters change.
  useEffect(() => setVisible(PAGE_SIZE), [selectedCategory, selectedType, searchQuery]);

  // Render more cards as the user approaches the end of the grid.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || visible >= filteredVideos.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries[0].isIntersecting && setVisible((v) => v + PAGE_SIZE),
      { rootMargin: "800px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible, filteredVideos.length]);

  const close = useCallback(() => setOpenVideo(null), []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-4 px-4 w-full">
        {Array.from({ length: 9 }).map((_, i) => (
          <SkeletonVideoCard key={i} />
        ))}
      </div>
    );
  }
  if (error) return <div className="px-4">We couldn't load the videos. Refresh the page to try again.</div>;

  return (
    <>
      <VideoQuickView video={openVideo} onClose={close} />
      <div className="max-w-[100vw] w-full flex flex-col justify-center items-center mx-auto">
        {filteredVideos.length === 0 ? (
          <p className="px-4 py-12 text-neutral-600 w-full">
            No videos match {searchQuery ? `“${searchQuery}”` : "these filters"} yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-4 px-4 w-full">
            {filteredVideos.slice(0, visible).map((video) => (
              <VideoCard key={video.id} video={video} onOpen={setOpenVideo} isAdmin={isAdmin} />
            ))}
          </div>
        )}
        <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />
      </div>
    </>
  );
};

export default Videos;
